import { t, type Locale } from '@sd/i18n';
import {
  PIRATES,
  hexToPixel,
  isExplored,
  sectorOf,
  seenFleets,
  visionInput,
  type GameState,
} from '@sd/sim';
import { PIRATE_COLOR, PLANET_COLORS, SCENE_COLORS, seatColorHex } from '../palette';

/**
 * The minimap (#55, docs/design/ui-style.md): the whole system from above,
 * without perspective — capitals and outposts in seat colours, pirates,
 * dust clouds, the sector a crisis will strike, and the camera's footprint.
 * A click sends the camera there; three buttons zoom and fly home.
 */
export interface MinimapHandlers {
  /** A point of the map plane, in world units. */
  lookAt(x: number, z: number): void;
  zoom(factor: number): void;
  home(): void;
}

const WIDTH = 284;
const HEIGHT = 196;

const ICONS = {
  minus: '<path d="M5 12h14"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  home: '<path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z"/><circle cx="12" cy="12" r="2.5"/>',
};
const icon = (name: keyof typeof ICONS) =>
  `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name]}</svg>`;

export class Minimap {
  private readonly root: HTMLElement;
  private readonly map: HTMLCanvasElement;
  private readonly view: HTMLCanvasElement;
  private scale = 1;
  private lastDraw = -Infinity;

  constructor(
    parent: HTMLElement,
    locale: Locale,
    private readonly me: string,
    on: MinimapHandlers,
  ) {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.root = document.createElement('section');
    this.root.className = 'sd-panel sd-minimap';
    this.root.setAttribute('aria-label', t(locale, 'ui.map.minimap'));
    this.root.innerHTML = `
      <div class="sd-minimap__canvas">
        <canvas width="${WIDTH * dpr}" height="${HEIGHT * dpr}"></canvas>
        <canvas width="${WIDTH * dpr}" height="${HEIGHT * dpr}"></canvas>
      </div>
      <div class="sd-minimap__tools">
        <button class="sd-btn sd-btn--icon" type="button" data-zoom="1.4" title="${t(locale, 'ui.map.zoomOut')}">${icon('minus')}</button>
        <button class="sd-btn sd-btn--icon" type="button" data-zoom="0.7" title="${t(locale, 'ui.map.zoomIn')}">${icon('plus')}</button>
        <button class="sd-btn sd-btn--icon" type="button" data-home title="${t(locale, 'ui.map.toCapital')}">${icon('home')}</button>
      </div>`;
    parent.append(this.root);
    [this.map, this.view] = [...this.root.querySelectorAll('canvas')] as [
      HTMLCanvasElement,
      HTMLCanvasElement,
    ];
    for (const c of [this.map, this.view]) {
      c.style.width = `${WIDTH}px`;
      c.style.height = `${HEIGHT}px`;
      c.getContext('2d')!.scale(dpr, dpr);
    }
    this.root.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest('button');
      if (button?.dataset.zoom) on.zoom(Number(button.dataset.zoom));
      else if (button && 'home' in button.dataset) on.home();
    });
    this.view.addEventListener('pointerdown', (event) => {
      const rect = this.view.getBoundingClientRect();
      const x = (event.clientX - rect.left - WIDTH / 2) / this.scale;
      const z = (event.clientY - rect.top - HEIGHT / 2) / this.scale;
      on.lookAt(x, z);
    });
  }

  /** Redraws the map at most twice a second: it changes slowly. */
  update(state: GameState, crisisSector: number | null | undefined, now: number) {
    if (now - this.lastDraw < 500) return;
    this.lastDraw = now;
    const ctx = this.map.getContext('2d')!;
    const R = state.system.mapRadius;
    this.scale = Math.min(WIDTH / (Math.sqrt(3) * (2 * R + 1)), HEIGHT / (3 * R + 2));
    const s = this.scale;
    const at = (h: { q: number; r: number }) => {
      const p = hexToPixel(h);
      return [WIDTH / 2 + p.x * s, HEIGHT / 2 + p.y * s] as const;
    };
    const dot = (h: { q: number; r: number }, r: number, color: string) => {
      const [x, y] = at(h);
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = SCENE_COLORS.outline;
      ctx.lineWidth = 1;
      ctx.stroke();
    };
    ctx.clearRect(0, 0, WIDTH, HEIGHT);
    // The classic fog (#97): what the player has never seen stays dark and empty.
    const known = (h: { q: number; r: number }) => isExplored(state, this.me, h);
    const rings = new Set(state.system.orbits.map((o) => o.ringRadius));
    for (let q = -R; q <= R; q++) {
      for (let r = Math.max(-R, -q - R); r <= Math.min(R, -q + R); r++) {
        const ring = (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2;
        const struck =
          crisisSector !== undefined &&
          (crisisSector === null || sectorOf({ q, r }) === crisisSector);
        ctx.fillStyle = !known({ q, r })
          ? '#0a0d16'
          : struck
            ? '#4a3424'
            : rings.has(ring)
              ? '#26324f'
              : '#161c2c';
        const [x, y] = at({ q, r });
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const a = ((60 * i - 30) * Math.PI) / 180;
          ctx.lineTo(x + s * 0.92 * Math.cos(a), y + s * 0.92 * Math.sin(a));
        }
        ctx.fill();
      }
    }
    for (const cloud of state.system.clouds) {
      // A cloud scanned to the end has dispersed (#103).
      if ((state.exploration.findsLeft[cloud.id] ?? 0) <= 0) continue;
      for (const h of cloud.hexes) if (known(h)) dot(h, s * 0.8, 'rgba(181,155,224,0.45)');
    }
    dot({ q: 0, r: 0 }, s * 1.5, '#fff1d6');
    // Lagrange points (#127), in the exotics' violet.
    for (const l of state.system.lagrangePoints) if (known(l.hex)) dot(l.hex, s * 0.7, '#d78cff');
    for (const p of state.system.planets) {
      if (!known(p.hex)) continue;
      dot(
        p.hex,
        p.homeOf ? s * 1.4 : s * 0.8,
        p.homeOf ? seatColorHex(p.homeOf) : PLANET_COLORS[p.kind],
      );
    }
    for (const o of state.outposts) {
      if (o.owner === this.me || known(o.hex)) dot(o.hex, s * 0.75, seatColorHex(o.owner));
    }
    for (const f of seenFleets(visionInput(state), this.me)) {
      dot(f.hex, s * 0.55, f.owner === PIRATES ? PIRATE_COLOR : seatColorHex(f.owner));
    }
  }

  /** The camera's footprint, every frame, on its own layer. */
  drawView(corners: readonly { x: number; z: number }[]) {
    const ctx = this.view.getContext('2d')!;
    ctx.clearRect(0, 0, WIDTH, HEIGHT);
    if (corners.length < 3) return;
    ctx.beginPath();
    for (const c of corners)
      ctx.lineTo(WIDTH / 2 + c.x * this.scale, HEIGHT / 2 + c.z * this.scale);
    ctx.closePath();
    ctx.strokeStyle = '#e6eaf5';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  dispose() {
    this.root.remove();
  }
}
